import { type AlertDialogOptions, LuIcon, useAlertDialog } from "@overmap-ai/blocks"
import { isAbortError, type RequiredFileModel, type Stored, type UUID } from "@overmap-ai/common"
import { BaseOfflineFileModelApiService } from "@overmap-ai/core"
import { useAbortableEffect, useActiveEffect, useSyncedState } from "@overmap-ai/hooks"
import Fuse, { type IFuseOptions } from "fuse.js"
import {
	type ChangeEventHandler,
	type DependencyList,
	type FocusEventHandler,
	type RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react"
import { type DebouncedState, useDebouncedCallback } from "use-debounce"

import { modelIconMapping } from "../mappings/icons"
import { AppSDK, sdk } from "../sdk"
import type { RootState } from "../store"
import { getFilesAndPromisesFromAttachments } from "../utils/file"

export const useMountedGuard = () => {
	const [mounted, setMounted] = useState(false)

	useEffect(() => {
		setMounted(true)
	}, [])

	return mounted
}

export const useFileInputRef = (): [RefObject<HTMLInputElement | null>, () => void] => {
	const fileInputRef = useRef<HTMLInputElement>(null)

	const handleTriggerFileInputRef = useCallback(() => {
		if (fileInputRef.current) fileInputRef.current.click()
	}, [])

	return [fileInputRef, handleTriggerFileInputRef]
}

export const useConfirmDeleteDialog = () => {
	const openAlertDialog = useAlertDialog()

	return useCallback(
		({
			title,
			description,
			onAction,
			onCancel,
		}: Pick<AlertDialogOptions, "title" | "description" | "onAction" | "onCancel">) => {
			return openAlertDialog({
				title,
				description,
				onAction,
				onCancel,
				size: "sm",
				action: (
					<>
						<LuIcon icon={modelIconMapping.delete} /> Delete
					</>
				),
				cancel: (
					<>
						<LuIcon icon={modelIconMapping.close} /> Cancel
					</>
				),
			})
		},
		[openAlertDialog],
	)
}

export const useConfirmSubmitDialog = () => {
	const openAlertDialog = useAlertDialog()

	return useCallback(
		({
			title,
			description,
			onAction,
			onCancel,
		}: Pick<AlertDialogOptions, "title" | "description" | "onAction" | "onCancel">) => {
			return openAlertDialog({
				title,
				description,
				onAction,
				onCancel,
				size: "sm",
				action: (
					<>
						<LuIcon icon={modelIconMapping.check} /> Submit
					</>
				),
				cancel: (
					<>
						<LuIcon icon={modelIconMapping.close} /> Cancel
					</>
				),
			})
		},
		[openAlertDialog],
	)
}

export const useResolvePromise = <TValue, TPromise extends Promise<TValue>>(
	valueOrPromise: TValue | TPromise,
): [TValue | null, string | null] => {
	const [resolved, setResolved] = useState<TValue | null>(
		valueOrPromise instanceof Promise ? null : (valueOrPromise as TValue),
	)
	const [error, setError] = useState<string | null>(null)

	useActiveEffect(
		(isActive) => {
			setError(null)

			if (!(valueOrPromise instanceof Promise)) {
				setResolved(valueOrPromise)
				return
			}

			setResolved(null)

			void valueOrPromise
				.then((value) => {
					if (isActive()) setResolved(value)
				})
				.catch((error: unknown) => {
					if (isActive()) {
						setError(error instanceof Error ? error.message : "Unknown error")
					}
				})
		},
		[valueOrPromise],
	)

	return [resolved, error]
}

export const useFetchFile = (url: string, sha1: string, name: string, blockFetch?: boolean): [File | null, boolean] => {
	const [failed, setFailed] = useState<boolean>(false)
	const [resolvedFile, setResolvedFile] = useState<File | null>(null)

	useAbortableEffect(
		(abortController) => {
			setResolvedFile(null)
			setFailed(false)

			if (blockFetch) return

			const fetchFile = async () => {
				try {
					const file = await sdk.files.fetchFileFromUrl(url, sha1, name, abortController.signal)
					if (abortController.signal.aborted) return
					setResolvedFile(file)
				} catch (e: unknown) {
					if (isAbortError(e) || abortController.signal.aborted) return
					setFailed(true)
				}
			}

			void fetchFile()
		},
		[blockFetch, name, sha1, url],
	)

	return [resolvedFile, failed]
}

export const useFetchFileUrlFromFileModel = <TFileModel extends UUID<RequiredFileModel>>(
	fileModel: Stored<TFileModel>,
	service: BaseOfflineFileModelApiService<string, TFileModel, RootState, AppSDK>,
	blockFetch?: boolean,
): [string | null, boolean] => {
	const [fileUrl, setFileUrl] = useState<string | null>(null)
	const [failed, setFailed] = useState<boolean>(false)

	useAbortableEffect(
		(abortController) => {
			setFileUrl(null)
			setFailed(false)

			if (blockFetch) return

			const fetchFileUrl = async () => {
				try {
					const fileUrl = await service.getPresignedUrl(fileModel.uuid, abortController.signal)
					if (abortController.signal.aborted) return
					setFileUrl(fileUrl)
				} catch (e: unknown) {
					if (isAbortError(e) || abortController.signal.aborted) return
					setFailed(true)
				}
			}

			void fetchFileUrl()
		},
		[blockFetch, service, fileModel.uuid],
	)

	return [fileUrl, failed]
}

export const useFetchFileFromFileModel = <TFileModel extends UUID<RequiredFileModel>>(
	fileModel: Stored<TFileModel>,
	service: BaseOfflineFileModelApiService<string, TFileModel, RootState, AppSDK>,
	blockFetch?: boolean,
): [File | null, boolean] => {
	const [fileUrl, fileUrlFailed] = useFetchFileUrlFromFileModel(fileModel, service, blockFetch)

	const [file, fileFailed] = useFetchFile(
		fileUrl ?? "",
		fileModel.file_sha1,
		fileModel.file_name,
		blockFetch || !fileUrl,
	)

	const computedFailed = fileUrlFailed || fileFailed || false

	return [file, computedFailed]
}

export const useBlurInput = <
	TElement extends HTMLTextAreaElement | HTMLInputElement = HTMLTextAreaElement | HTMLInputElement,
>(
	value: NonNullable<TElement["value"]>,
	onBlur: FocusEventHandler<TElement>,
	skipIfEqual?: boolean,
) => {
	const [controlledValue, setControlledValue] = useSyncedState<TElement["value"]>(value)

	const handleChange = useCallback<ChangeEventHandler<TElement>>(
		(e) => {
			setControlledValue(e.target.value)
		},
		[setControlledValue],
	)

	const handleBlur = useCallback<FocusEventHandler<TElement>>(
		(e) => {
			if (skipIfEqual && e.target.value === value) return
			onBlur(e)
		},
		[onBlur, value, skipIfEqual],
	)

	return { value: controlledValue, onChange: handleChange, onBlur: handleBlur }
}

export const useOnlineStatus = () => {
	const [online, setOnline] = useState<boolean>(() => navigator.onLine)

	const handleOnline = useCallback(() => {
		setOnline(true)
	}, [])

	const handleOffline = useCallback(() => {
		setOnline(false)
	}, [])

	useEffect(() => {
		window.addEventListener("online", handleOnline)
		window.addEventListener("offline", handleOffline)

		return () => {
			window.removeEventListener("online", handleOnline)
			window.removeEventListener("offline", handleOffline)
		}
	}, [handleOffline, handleOnline])

	return online
}

// TODO: Move
// Custom hook for getting the user's current bearing
// https://github.com/bence-toth/react-hook-geolocation
export const useGeolocation = () => {
	const [coordinates, setCoordinates] = useState({
		accuracy: undefined,
		altitude: undefined,
		altitudeAccuracy: undefined,
		heading: undefined,
		latitude: undefined,
		longitude: undefined,
		speed: undefined,
	} as Partial<GeolocationCoordinates>)

	useEffect(() => {
		let didCancel: boolean
		const updateCoordinates: PositionCallback = (position: GeolocationPosition) => {
			const { coords } = position
			const { accuracy, altitude, altitudeAccuracy, heading, latitude, longitude, speed } = coords
			if (!didCancel) {
				setCoordinates({
					accuracy,
					altitude,
					altitudeAccuracy,
					heading,
					latitude,
					longitude,
					speed,
				})
			}
		}

		const setError = () => {
			if (!didCancel) {
				setCoordinates({
					accuracy: undefined,
					altitude: undefined,
					altitudeAccuracy: undefined,
					heading: undefined,
					latitude: undefined,
					longitude: undefined,
					speed: undefined,
				})
			}
		}

		let watchId: number | undefined
		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(updateCoordinates, setError)
			watchId = navigator.geolocation.watchPosition(updateCoordinates, setError, {
				enableHighAccuracy: true,
				maximumAge: 15000,
				timeout: 12000,
			})
		}
		return () => {
			if (watchId) {
				navigator.geolocation.clearWatch(watchId)
			}
			didCancel = true
		}
	}, [])

	return coordinates
}

export const usePagination = <TItem,>(items: TItem[], itemsPerPage: number) => {
	const [currentPage, setCurrentPage] = useState(0)

	const pageCount = useMemo(() => Math.ceil(items.length / itemsPerPage), [items, itemsPerPage])

	const page = useMemo(
		() => items.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage),
		[currentPage, items, itemsPerPage],
	)

	useEffect(() => {
		const lastPageIndex = Math.max(pageCount - 1, 0)
		if (currentPage > lastPageIndex) setCurrentPage(lastPageIndex)
	}, [currentPage, pageCount])

	const nextPage = useCallback(() => {
		setCurrentPage((prevCurrentPage) => Math.min(prevCurrentPage + 1, pageCount - 1))
	}, [pageCount])

	const prevPage = useCallback(() => {
		setCurrentPage((prevCurrentPage) => Math.max(prevCurrentPage - 1, 0))
	}, [])

	const firstPage = useCallback(() => {
		setCurrentPage(0)
	}, [])

	const lastPage = useCallback(() => {
		setCurrentPage(Math.max(pageCount - 1, 0))
	}, [pageCount])

	return useMemo(
		() => ({
			page,
			pageCount,
			currentPage,
			setCurrentPage,
			nextPage,
			prevPage,
			firstPage,
			lastPage,
		}),
		[currentPage, firstPage, lastPage, nextPage, page, pageCount, prevPage],
	)
}

export function useFuse<T>(list: T[], searchTerm: string, fuseOptions?: IFuseOptions<T>) {
	const fuse = useMemo(() => {
		return new Fuse(list, fuseOptions)
	}, [list, fuseOptions])

	return useMemo(() => {
		if (searchTerm === "") return list
		return fuse.search(searchTerm).map(({ item }) => item)
	}, [fuse, list, searchTerm])
}

export const useList = <TItem,>(initialItems: TItem[]) => {
	const [items, setItems] = useState<TItem[]>(initialItems)

	const add = useCallback((item: TItem) => {
		setItems((prev) => [...prev, item])
	}, [])

	const addMany = useCallback((newItems: TItem[]) => {
		setItems((prev) => [...prev, ...newItems])
	}, [])

	const remove = useCallback((index: number) => {
		setItems((prev) => prev.filter((_, i) => i !== index))
	}, [])

	const removeBy = useCallback((predicate: (item: TItem) => boolean) => {
		setItems((prev) => prev.filter((item) => !predicate(item)))
	}, [])

	const update = useCallback((index: number, item: TItem) => {
		setItems((prev) => prev.map((existing, i) => (i === index ? item : existing)))
	}, [])

	const updateBy = useCallback((predicate: (item: TItem) => boolean, updater: (item: TItem) => TItem) => {
		setItems((prev) => prev.map((item) => (predicate(item) ? updater(item) : item)))
	}, [])

	const clear = useCallback(() => {
		setItems([])
	}, [])

	const reset = useCallback(() => {
		setItems(initialItems)
	}, [initialItems])

	return useMemo(
		() => ({
			items,
			setItems,
			add,
			addMany,
			remove,
			removeBy,
			update,
			updateBy,
			clear,
			reset,
			isEmpty: items.length === 0,
			length: items.length,
		}),
		[items, add, addMany, remove, removeBy, update, updateBy, clear, reset],
	)
}

export const useChangeEventHandler = <TElement extends HTMLTextAreaElement | HTMLInputElement>(
	callback: (value: string) => void,
) => {
	return useCallback<ChangeEventHandler<TElement>>(
		(e) => {
			callback(e.target.value)
		},
		[callback],
	)
}

// oxlint-disable-next-line no-explicit-any REASON: any is fine here for a generic function
export function useDebouncedCallbackWithFlush<TFunction extends (...args: any[]) => any>(
	callback: TFunction,
	delay: number,
	deps: DependencyList,
	debounceOptions?: Parameters<typeof useDebouncedCallback>[2],
): DebouncedState<TFunction> {
	// oxlint-disable-next-line exhaustive-deps REASON: specififies own deps array
	const memoizedCallback = useCallback(callback, deps)

	const debounced = useDebouncedCallback(memoizedCallback, delay, debounceOptions)

	useEffect(() => {
		return () => {
			debounced.flush()
		}
	}, [debounced])

	return debounced
}

export const useFilesAndPromisesFromAttachments = <TAttachment extends UUID<RequiredFileModel>>(
	attachments: TAttachment[],
	service: BaseOfflineFileModelApiService<string, TAttachment, RootState, AppSDK>,
) => {
	return useMemo(() => {
		return getFilesAndPromisesFromAttachments<TAttachment>(attachments, service)
	}, [attachments, service])
}
